import dayjs from 'dayjs';
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
  type HTMLAttributes,
  type Key,
} from 'react';
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { alpha } from '@mui/material/styles';
import {
  Refresh,
  PhoneInTalk,
  Clear,
  CallReceived,
  Campaign,
  Dialpad,
  Phone,
  Person,
  Assessment,
  HelpOutline,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatPhone } from '../utils/phone';
import { useListSearchParams } from '../hooks/useListSearchParams';
import { useLanguage } from '../hooks/useLanguage';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import usePageTitle from '../hooks/usePageTitle';
import { useCallDetailDrawer } from '../contexts/CallDetailDrawerContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import type { CallConsumptionSegment, CallSegmentTotals, CallSegmentType, CallStatus } from '../types';
import CallStatusChip from '../components/CallStatusChip';
import CallConsumptionDisplay from '../components/CallConsumptionDisplay';
import CreateCallReportDialog, { type CallReportSummaryEntry } from '../components/reports/CreateCallReportDialog';

interface CallListItem {
  id: string;
  direction: 'outbound' | 'inbound';
  origin: string;
  callType?: 'ai_agent' | 'reverse_ivr' | 'sip';
  toNumber: string;
  fromNumber: string;
  status: CallStatus;
  duration: number;
  durationFormatted?: string;
  ivrSelectedDigit?: string | null;
  billedSeconds?: number;
  consumptionSegments?: CallConsumptionSegment[];
  activeSegmentId?: string | null;
  activeSegmentType?: CallSegmentType | null;
  segmentTotals?: CallSegmentTotals | null;
  campaignId?: string;
  campaignName?: string;
  campaignContactId?: string;
  campaignContactDisplayName?: string;
  agentId?: string;
  agentName?: string;
  createdById?: string;
  createdByName?: string | null;
  createdByExtension?: string | null;
  isConverted?: boolean;
  isOptout?: boolean;
  isMismatch?: boolean;
  originLineName?: string;
  phoneLabel?: string | null;
  createdAt: string;
  initiatedAt?: string;
}

type CallDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';
type CallBooleanFilterValue = 'true' | 'false';

interface CallFilterOption {
  value: string;
  label: string;
}

type CallStatusGroup = 'transient' | 'intermediate' | 'terminal';

interface CallStatusFilterOption extends CallFilterOption {
  group: CallStatusGroup;
}

interface CallDateRangeOption extends CallFilterOption {
  value: CallDateRangePreset;
}

interface CallParticipantApiOption {
  id: string;
  username?: string | null;
  full_name?: string | null;
  fullName?: string | null;
  email?: string | null;
}

interface CallCampaignApiOption {
  id: string;
  name?: string | null;
}

interface CallAiAgentApiOption {
  id: string;
  name?: string | null;
}

interface CallGeoFilterApiOption {
  value: string;
  label?: string;
}

interface CallGeoFilterOptionsResponse {
  regions?: CallGeoFilterApiOption[];
  states?: CallGeoFilterApiOption[];
  ddds?: CallGeoFilterApiOption[];
}

interface CallCarrierFilterOptionsResponse {
  carriers?: CallGeoFilterApiOption[];
}

type CallOutcomeFilterValue =
  | 'converted'
  | 'mismatch'
  | 'optout'
  | 'ivr_heard_to_end'
  | 'not_converted'
  | 'not_mismatch'
  | 'not_optout';
type CallBinaryFilterKey = 'has_transfer' | 'has_human_answer' | 'has_ai_answer' | 'has_ai_refund';
type CallOriginFilterValue = 'campaign' | 'manual' | 'public_api' | 'inbound_route';
type CallTypeFilterValue = 'ai_agent' | 'reverse_ivr' | 'sip';
type CallDirectionFilterValue = 'outbound' | 'inbound';

const FILTER_MULTI_VALUE_SEPARATOR = ',';
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARACTERS = 3;
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FILTER_PARAM_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const PARTICIPANT_AUTOCOMPLETE_LIMIT = 10;
const PARTICIPANT_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const CAMPAIGN_AUTOCOMPLETE_LIMIT = 10;
const CAMPAIGN_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const AI_AGENT_AUTOCOMPLETE_LIMIT = 10;
const AI_AGENT_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const FILTER_MIN_WIDTH_ORIGIN = 220;
const FILTER_MIN_WIDTH_DIRECTION = 220;
const FILTER_MIN_WIDTH_CALL_TYPE = 220;
const FILTER_MIN_WIDTH_STATUS = 220;
const FILTER_MIN_WIDTH_OUTCOME = 220;
const FILTER_MIN_WIDTH_BOOLEAN = 220;
const FILTER_MIN_WIDTH_AI_ANSWER = 240;
const FILTER_MIN_WIDTH_AI_AGENT = 260;
const FILTER_MIN_WIDTH_DTMF = 220;
const FILTER_MIN_WIDTH_CAMPAIGN = 260;
const FILTER_MIN_WIDTH_PARTICIPANT = 260;
const FILTER_MIN_WIDTH_GEO = 220;
const FILTER_MIN_WIDTH_PHONE_TYPE = 220;
const FILTER_MIN_WIDTH_CARRIER = 260;
const FILTER_MIN_WIDTH_DATE_RANGE = 220;
const FILTER_MIN_WIDTH_SEARCH = 320;

const CALL_DATE_RANGE_PRESETS: readonly CallDateRangePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'period',
];
const CALL_OUTCOME_FILTER_VALUES: readonly CallOutcomeFilterValue[] = [
  'converted',
  'mismatch',
  'optout',
  'ivr_heard_to_end',
  'not_converted',
  'not_mismatch',
  'not_optout',
];
const CALL_BOOLEAN_FILTER_VALUES: readonly CallBooleanFilterValue[] = ['true', 'false'];
const CALL_ORIGIN_FILTER_VALUES: readonly CallOriginFilterValue[] = [
  'campaign',
  'manual',
  'public_api',
  'inbound_route',
];
const CALL_TYPE_FILTER_VALUES: readonly CallTypeFilterValue[] = ['ai_agent', 'reverse_ivr', 'sip'];
const CALL_DIRECTION_FILTER_VALUES: readonly CallDirectionFilterValue[] = ['outbound', 'inbound'];
const CALL_PHONE_TYPE_FILTER_VALUES = ['mobile', 'landline', 'sip', 'unknown'] as const;
const START_TIME_ORDERING_FIELDS = ['activity_at', 'created_at', 'initiated_at'] as const;
const CALL_BINARY_FILTER_AI_ANSWER: CallBinaryFilterKey = 'has_ai_answer';
const CALL_BINARY_FILTER_AI_REFUND: CallBinaryFilterKey = 'has_ai_refund';
const REVERSE_IVR_DTMF_DIGITS: readonly string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const CALL_STATUS_GROUP_ORDER: Record<CallStatusGroup, number> = {
  transient: 0,
  intermediate: 1,
  terminal: 2,
};
const CALL_TRANSIENT_STATUSES: readonly CallStatus[] = [
  'pending',
  'waiting_capacity',
  'waiting_operating_hours',
  'queued',
  'dialing',
  'analyzing',
  'ringing',
  'in_progress',
];
const CALL_INTERMEDIATE_STATUSES: readonly CallStatus[] = ['finalizing'];
const CALL_TERMINAL_STATUSES: readonly CallStatus[] = [
  'completed',
  'no_interaction',
  'failed',
  'no_answer',
  'no_answer_timeout',
  'busy',
  'cancelled',
  'transfer_unanswered',
  'congestion',
  'voicemail',
  'invalid_number',
  'config_error',
  'declined',
  'unreachable',
  'timeout',
  'mismatch',
  'api_timeout',
  'terminated_manual',
  'capacity_timeout',
  'operating_window_timeout',
];

function buildFilterFieldSx(minWidth: number) {
  return {
    minWidth,
    flex: `1 1 ${minWidth}px`,
  };
}

function isCallDateRangePreset(value: string | null | undefined): value is CallDateRangePreset {
  if (!value) {
    return false;
  }
  return CALL_DATE_RANGE_PRESETS.includes(value as CallDateRangePreset);
}

function getCallStatusGroup(value: CallStatus): CallStatusGroup {
  if (CALL_INTERMEDIATE_STATUSES.includes(value)) {
    return 'intermediate';
  }
  if (CALL_TERMINAL_STATUSES.includes(value)) {
    return 'terminal';
  }
  return 'transient';
}

function formatCallDurationValue(call: Pick<CallListItem, 'duration' | 'durationFormatted'>): string {
  const preformatted = String(call.durationFormatted || '').trim();
  if (preformatted) {
    return preformatted;
  }

  const totalSeconds = Math.max(0, Math.round(Number(call.duration || 0)));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function renderCheckboxFilterOption(
  props: HTMLAttributes<HTMLLIElement> & { key: Key },
  label: string,
  selected: boolean,
) {
  const { key, ...optionProps } = props;
  return (
    <Box component="li" key={key} {...optionProps}>
      <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
      {label}
    </Box>
  );
}

export default function CallsPage() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:calls.title'));
  const { capabilities } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);
  const [calls, setCalls] = useState<CallListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCalls, setTotalCalls] = useState(0);
  const [isCreateCallReportOpen, setIsCreateCallReportOpen] = useState(false);
  const { activeCallId: selectedCallId } = useCallDetailDrawer();
  const hasAiSubscription = capabilities?.hasAiSubscription ?? false;
  const hasReverseIvrSubscription = capabilities?.hasReverseIvrSubscription ?? false;
  const hasTelephonyPack = capabilities?.hasTelephonyPack ?? false;
  const availableCallSurfaceCount = (
    Number(hasAiSubscription)
    + Number(hasReverseIvrSubscription)
    + Number(hasTelephonyPack)
  );
  const showConsumptionColumn = hasAiSubscription || hasTelephonyPack;
  const showReverseIvrDigitColumn = hasReverseIvrSubscription;
  const showCallTypeColumn = availableCallSurfaceCount > 1;
  const showCallTypeFilter = showCallTypeColumn;
  const canCreateCallReport = hasPermission('calls_reports_access');

  const getBilledSeconds = useCallback((call: CallListItem) => {
    if (call.segmentTotals?.totalBilledSeconds !== undefined && call.segmentTotals?.totalBilledSeconds !== null) {
      return call.segmentTotals.totalBilledSeconds;
    }
    if (typeof call.billedSeconds === 'number') {
      return call.billedSeconds;
    }
    return call.duration ?? 0;
  }, []);

  const getEffectiveSeconds = useCallback((call: CallListItem) => {
    return call.duration ?? 0;
  }, []);
  const [searchParams] = useSearchParams();
  const callRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const participantRequestIdRef = useRef(0);
  const campaignRequestIdRef = useRef(0);
  const aiAgentRequestIdRef = useRef(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const mobileCardShadow = isDarkMode ? '0 20px 35px rgba(0,0,0,0.6)' : '0 15px 30px rgba(0,0,0,0.08)';
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);

  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
    '& .MuiPickersInputBase-root': {
      padding: '3px 14px 2px 14px',
    },
  };

  const parseMultiFilterValue = useCallback((rawValue: string | null | undefined): string[] => {
    if (!rawValue) {
      return [];
    }
    const normalizedValues = rawValue
      .split(FILTER_MULTI_VALUE_SEPARATOR)
      .map((value) => value.trim())
      .filter(Boolean);
    return Array.from(new Set(normalizedValues));
  }, []);

  const serializeMultiFilterValue = useCallback((values: string[]): string | undefined => {
    const normalizedValues = values
      .map((value) => value.trim())
      .filter(Boolean);
    if (normalizedValues.length === 0) {
      return undefined;
    }
    return normalizedValues.join(FILTER_MULTI_VALUE_SEPARATOR);
  }, []);

  const resolveSelectedOptions = useCallback(
    <T extends CallFilterOption>(selectedValues: string[], options: T[]): T[] => {
      const optionsByValue = new Map(options.map((option) => [option.value, option]));
      return selectedValues
        .map((value) => optionsByValue.get(value))
        .filter((option): option is T => Boolean(option));
    },
    [],
  );
  const resolveSingleSelectedOption = useCallback(
    <T extends CallFilterOption>(selectedValue: string | null | undefined, options: T[]): T | null => {
      if (!selectedValue) {
        return null;
      }
      return options.find((option) => option.value === selectedValue) ?? null;
    },
    [],
  );

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['campaign', 'origin', 'direction', 'call_type', 'ai_agent_id', 'status', 'outcome', 'has_transfer', 'has_human_answer', 'has_ai_answer', 'has_ai_refund', 'ivr_selected_digit', 'participant_user', 'region', 'state_code', 'ddd', 'phone_type', 'carrier', 'search', 'date_range', 'start_date', 'end_date'],
    defaultPage: 1,
    defaultPageSize: 25,
    defaultOrdering: '-created_at',
  });

  const campaignFilterValue = filters.campaign ?? '';
  const originFilterValues = useMemo(() => parseMultiFilterValue(filters.origin), [filters.origin, parseMultiFilterValue]);
  const directionFilterValues = useMemo(() => parseMultiFilterValue(filters.direction), [filters.direction, parseMultiFilterValue]);
  const callTypeFilterValues = useMemo(() => parseMultiFilterValue(filters.call_type), [filters.call_type, parseMultiFilterValue]);
  const aiAgentFilterValues = useMemo(() => parseMultiFilterValue(filters.ai_agent_id), [filters.ai_agent_id, parseMultiFilterValue]);
  const statusFilterValues = useMemo(() => parseMultiFilterValue(filters.status), [filters.status, parseMultiFilterValue]);
  const outcomeFilterValues = useMemo(() => parseMultiFilterValue(filters.outcome), [filters.outcome, parseMultiFilterValue]);
  const transferFilterValue = filters.has_transfer ?? '';
  const humanAnswerFilterValue = filters.has_human_answer ?? '';
  const aiAnswerFilterValue = filters.has_ai_answer ?? '';
  const aiRefundFilterValue = filters.has_ai_refund ?? '';
  const ivrSelectedDigitFilterValues = useMemo(
    () => parseMultiFilterValue(filters.ivr_selected_digit),
    [filters.ivr_selected_digit, parseMultiFilterValue],
  );
  const participantFilterValues = useMemo(
    () => parseMultiFilterValue(filters.participant_user),
    [filters.participant_user, parseMultiFilterValue],
  );
  const regionFilterValues = useMemo(
    () => parseMultiFilterValue(filters.region),
    [filters.region, parseMultiFilterValue],
  );
  const stateCodeFilterValues = useMemo(
    () => parseMultiFilterValue(filters.state_code),
    [filters.state_code, parseMultiFilterValue],
  );
  const dddFilterValues = useMemo(
    () => parseMultiFilterValue(filters.ddd),
    [filters.ddd, parseMultiFilterValue],
  );
  const phoneTypeFilterValues = useMemo(
    () => parseMultiFilterValue(filters.phone_type),
    [filters.phone_type, parseMultiFilterValue],
  );
  const carrierFilterValues = useMemo(
    () => parseMultiFilterValue(filters.carrier),
    [filters.carrier, parseMultiFilterValue],
  );
  const searchFilterValue = filters.search ?? '';
  const startDateFilterValue = filters.start_date ?? '';
  const endDateFilterValue = filters.end_date ?? '';
  const [participantOptions, setParticipantOptions] = useState<CallFilterOption[]>([]);
  const [campaignOptions, setCampaignOptions] = useState<CallFilterOption[]>([]);
  const [aiAgentOptions, setAiAgentOptions] = useState<CallFilterOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<CallFilterOption[]>([]);
  const [stateCodeOptions, setStateCodeOptions] = useState<CallFilterOption[]>([]);
  const [dddOptions, setDddOptions] = useState<CallFilterOption[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<CallFilterOption[]>([]);
  const [campaignSearchInput, setCampaignSearchInput] = useState('');
  const [aiAgentSearchInput, setAiAgentSearchInput] = useState('');
  const [participantSearchInput, setParticipantSearchInput] = useState('');
  const [isCampaignLoading, setIsCampaignLoading] = useState(false);
  const [isAiAgentLoading, setIsAiAgentLoading] = useState(false);
  const [isParticipantLoading, setIsParticipantLoading] = useState(false);
  const [isGeoOptionsLoading, setIsGeoOptionsLoading] = useState(false);
  const [isCarrierOptionsLoading, setIsCarrierOptionsLoading] = useState(false);
  const [isCampaignOpen, setIsCampaignOpen] = useState(false);
  const [isAiAgentOpen, setIsAiAgentOpen] = useState(false);
  const [isParticipantOpen, setIsParticipantOpen] = useState(false);
  const selectedDateRange = useMemo<CallDateRangePreset | null>(() => {
    if (isCallDateRangePreset(filters.date_range)) {
      return filters.date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.date_range, startDateFilterValue]);
  const startDateValue = startDateFilterValue ? dayjs(startDateFilterValue) : null;
  const endDateValue = endDateFilterValue ? dayjs(endDateFilterValue) : null;
  const [searchInput, setSearchInput] = useState(searchFilterValue);
  const shouldLoadCampaignOptions = isCampaignOpen || Boolean(campaignFilterValue);
  const shouldLoadAiAgentOptions = hasAiSubscription && (isAiAgentOpen || aiAgentFilterValues.length > 0);
  const shouldLoadParticipantOptions = hasTelephonyPack && (isParticipantOpen || participantFilterValues.length > 0);
  const sortOptionsByLabel = useCallback(
    (options: CallFilterOption[]): CallFilterOption[] =>
      [...options].sort((optionA, optionB) =>
        optionA.label.localeCompare(optionB.label, i18n.language, { sensitivity: 'base' }),
      ),
    [i18n.language],
  );
  const buildRegionOption = useCallback(
    (value: string): CallFilterOption => ({
      value,
      label: t(`pages:calls.filters.regionOptions.${value}`, value),
    }),
    [t],
  );
  const sortDddOptions = useCallback(
    (options: CallFilterOption[]): CallFilterOption[] =>
      [...options].sort((optionA, optionB) => {
        const left = Number.parseInt(optionA.value, 10);
        const right = Number.parseInt(optionB.value, 10);
        if (Number.isNaN(left) || Number.isNaN(right)) {
          return optionA.value.localeCompare(optionB.value, i18n.language, { sensitivity: 'base' });
        }
        return left - right;
      }),
    [i18n.language],
  );

  const statusOptions = useMemo(
    (): CallStatusFilterOption[] => {
      const options = [
        ...CALL_TRANSIENT_STATUSES,
        ...CALL_INTERMEDIATE_STATUSES,
        ...CALL_TERMINAL_STATUSES,
      ].map((value) => ({
        value,
        label: t(`pages:calls.statusLabels.${value}`, value),
        group: getCallStatusGroup(value),
      }));
      return options.sort((optionA, optionB) => {
        const groupDiff = CALL_STATUS_GROUP_ORDER[optionA.group] - CALL_STATUS_GROUP_ORDER[optionB.group];
        if (groupDiff !== 0) {
          return groupDiff;
        }
        return optionA.label.localeCompare(optionB.label, i18n.language, { sensitivity: 'base' });
      });
    },
    [i18n.language, t],
  );

  const outcomeOptions = useMemo(
    (): CallFilterOption[] =>
      sortOptionsByLabel(
        CALL_OUTCOME_FILTER_VALUES
        .filter((value) => hasReverseIvrSubscription || value !== 'ivr_heard_to_end')
        .map((value) => ({
          value,
          label: t(`pages:calls.outcomeLabels.${value}`, value),
        })),
      ),
    [hasReverseIvrSubscription, sortOptionsByLabel, t],
  );
  const reverseIvrDigitOptions = useMemo(
    (): CallFilterOption[] => REVERSE_IVR_DTMF_DIGITS.map((digit) => ({ value: digit, label: digit })),
    [],
  );
  const booleanFilterOptions = useMemo(
    (): CallFilterOption[] =>
      CALL_BOOLEAN_FILTER_VALUES.map((value) => ({
        value,
        label: value === 'true' ? t('common:common.yes') : t('common:common.no'),
      })),
    [t],
  );
  const originOptions = useMemo(
    (): CallFilterOption[] =>
      sortOptionsByLabel(
        CALL_ORIGIN_FILTER_VALUES.map((value) => ({
          value,
          label: t(`pages:calls.origin.${value}`, value),
        })),
      ),
    [sortOptionsByLabel, t],
  );
  const directionOptions = useMemo(
    (): CallFilterOption[] =>
      CALL_DIRECTION_FILTER_VALUES.map((value) => ({
        value,
        label: t(`pages:calls.direction.${value}`, value),
      })),
    [t],
  );
  const callTypeOptions = useMemo(
    (): CallFilterOption[] =>
      sortOptionsByLabel(
        CALL_TYPE_FILTER_VALUES
          .filter((value) => {
            if (value === 'ai_agent') {
              return hasAiSubscription;
            }
            if (value === 'reverse_ivr') {
              return hasReverseIvrSubscription;
            }
            return hasTelephonyPack;
          })
          .map((value) => ({
            value,
            label: t(`pages:calls.callTypeLabels.${value}`, value),
          })),
      ),
    [hasAiSubscription, hasReverseIvrSubscription, hasTelephonyPack, sortOptionsByLabel, t],
  );
  const callTypeOptionValues = useMemo(
    () => new Set(callTypeOptions.map((option) => option.value)),
    [callTypeOptions],
  );
  const phoneTypeOptions = useMemo(
    (): CallFilterOption[] =>
      sortOptionsByLabel(
        CALL_PHONE_TYPE_FILTER_VALUES.map((value) => ({
          value,
          label: t(`pages:calls.filters.phoneTypeOptions.${value}`, value),
        })),
      ),
    [sortOptionsByLabel, t],
  );

  const dateRangeOptions = useMemo(
    (): CallDateRangeOption[] => [
      { value: 'today', label: t('pages:calls.filters.today') },
      { value: 'yesterday', label: t('pages:calls.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:calls.filters.last7Days') },
      { value: 'period', label: t('pages:calls.filters.period') },
    ],
    [t],
  );

  const orderingParam = ordering || '-created_at';
  const activeOrderingField = orderingParam.startsWith('-') ? orderingParam.slice(1) : orderingParam;
  const activeOrderingDirection: 'asc' | 'desc' = orderingParam.startsWith('-') ? 'desc' : 'asc';
  const isStartTimeOrdering = START_TIME_ORDERING_FIELDS.includes(
    activeOrderingField as (typeof START_TIME_ORDERING_FIELDS)[number],
  );
  const handleOrderingChange = useCallback(
    (orderingField: string, aliases: readonly string[] = []) => {
      const isCurrentOrderingField =
        activeOrderingField === orderingField || aliases.includes(activeOrderingField);
      const nextDirection = isCurrentOrderingField && activeOrderingDirection === 'asc' ? 'desc' : 'asc';
      const nextOrdering = nextDirection === 'desc' ? `-${orderingField}` : orderingField;
      setParams({ ordering: nextOrdering }, { resetPage: true });
    },
    [activeOrderingDirection, activeOrderingField, setParams],
  );
  const participantOptionsByValue = useMemo(
    () => new Map(participantOptions.map((option) => [option.value, option])),
    [participantOptions],
  );
  const selectedParticipantOptions = useMemo(
    () =>
      participantFilterValues.map(
        (value) =>
          participantOptionsByValue.get(value) ?? {
            value,
            label: value,
          },
      ),
    [participantFilterValues, participantOptionsByValue],
  );
  const regionOptionsByValue = useMemo(
    () => new Map(regionOptions.map((option) => [option.value, option])),
    [regionOptions],
  );
  const selectedRegionOptions = useMemo(
    () =>
      regionFilterValues.map(
        (value) => regionOptionsByValue.get(value) ?? buildRegionOption(value),
      ),
    [buildRegionOption, regionFilterValues, regionOptionsByValue],
  );
  const stateCodeOptionsByValue = useMemo(
    () => new Map(stateCodeOptions.map((option) => [option.value, option])),
    [stateCodeOptions],
  );
  const selectedStateCodeOptions = useMemo(
    () =>
      stateCodeFilterValues.map(
        (value) =>
          stateCodeOptionsByValue.get(value) ?? {
            value,
            label: value,
          },
      ),
    [stateCodeFilterValues, stateCodeOptionsByValue],
  );
  const dddOptionsByValue = useMemo(
    () => new Map(dddOptions.map((option) => [option.value, option])),
    [dddOptions],
  );
  const selectedDddOptions = useMemo(
    () =>
      dddFilterValues.map(
        (value) =>
          dddOptionsByValue.get(value) ?? {
            value,
            label: value,
          },
      ),
    [dddFilterValues, dddOptionsByValue],
  );
  const selectedPhoneTypeOptions = useMemo(
    () => resolveSelectedOptions(phoneTypeFilterValues, phoneTypeOptions),
    [phoneTypeFilterValues, phoneTypeOptions, resolveSelectedOptions],
  );
  const carrierOptionsByValue = useMemo(
    () => new Map(carrierOptions.map((option) => [option.value, option])),
    [carrierOptions],
  );
  const selectedCarrierOptions = useMemo(
    () =>
      carrierFilterValues.map(
        (value) =>
          carrierOptionsByValue.get(value) ?? {
            value,
            label: value === 'unknown'
              ? t('pages:calls.filters.carrierUnknown')
              : value,
          },
      ),
    [carrierFilterValues, carrierOptionsByValue, t],
  );
  const selectedOriginOptions = useMemo(
    () => resolveSelectedOptions(originFilterValues, originOptions),
    [originFilterValues, originOptions, resolveSelectedOptions],
  );
  const selectedDirectionOptions = useMemo(
    () => resolveSelectedOptions(directionFilterValues, directionOptions),
    [directionFilterValues, directionOptions, resolveSelectedOptions],
  );
  const selectedCallTypeOptions = useMemo(
    () => resolveSelectedOptions(callTypeFilterValues, callTypeOptions),
    [callTypeFilterValues, callTypeOptions, resolveSelectedOptions],
  );
  const aiAgentOptionsByValue = useMemo(
    () => new Map(aiAgentOptions.map((option) => [option.value, option])),
    [aiAgentOptions],
  );
  const selectedAiAgentOptions = useMemo(
    () =>
      aiAgentFilterValues.map(
        (value) =>
          aiAgentOptionsByValue.get(value) ?? {
            value,
            label: value,
          },
      ),
    [aiAgentFilterValues, aiAgentOptionsByValue],
  );
  const campaignOptionsByValue = useMemo(
    () => new Map(campaignOptions.map((option) => [option.value, option])),
    [campaignOptions],
  );
  const selectedCampaignOption = useMemo(() => {
    if (!campaignFilterValue) {
      return null;
    }
    return campaignOptionsByValue.get(campaignFilterValue) ?? {
      value: campaignFilterValue,
      label: campaignFilterValue,
    };
  }, [campaignFilterValue, campaignOptionsByValue]);
  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );
  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || undefined,
        endDate: endDateFilterValue || undefined,
      };
    }

    if (!selectedDateRange) {
      return {
        startDate: undefined,
        endDate: undefined,
      };
    }

    const today = dayjs();
    if (selectedDateRange === 'today') {
      const date = today.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: date, endDate: date };
    }

    if (selectedDateRange === 'yesterday') {
      const date = today.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: date, endDate: date };
    }

    const endDate = today.format(DATE_FILTER_PARAM_FORMAT);
    const startDate = today.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT);
    return { startDate, endDate };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);
  const serializedActiveFilters = useMemo<Record<string, string>>(() => {
    const payload: Record<string, string> = {};
    if (campaignFilterValue) {
      payload.campaign = campaignFilterValue;
    }

    const originFilter = serializeMultiFilterValue(originFilterValues);
    if (originFilter) {
      payload.origin = originFilter;
    }

    const directionFilter = serializeMultiFilterValue(directionFilterValues);
    if (directionFilter) {
      payload.direction = directionFilter;
    }

    const callTypeFilter = showCallTypeFilter
      ? serializeMultiFilterValue(callTypeFilterValues)
      : undefined;
    if (callTypeFilter) {
      payload.call_type = callTypeFilter;
    }

    const aiAgentFilter = hasAiSubscription
      ? serializeMultiFilterValue(aiAgentFilterValues)
      : undefined;
    if (aiAgentFilter) {
      payload.ai_agent_id = aiAgentFilter;
    }

    const statusFilter = serializeMultiFilterValue(statusFilterValues);
    if (statusFilter) {
      payload.status = statusFilter;
    }

    const outcomeFilter = serializeMultiFilterValue(outcomeFilterValues);
    if (outcomeFilter) {
      payload.outcome = outcomeFilter;
    }

    if (hasTelephonyPack && transferFilterValue) {
      payload.has_transfer = transferFilterValue;
    }
    if (hasTelephonyPack && humanAnswerFilterValue) {
      payload.has_human_answer = humanAnswerFilterValue;
    }
    if (hasAiSubscription && aiAnswerFilterValue) {
      payload[CALL_BINARY_FILTER_AI_ANSWER] = aiAnswerFilterValue;
    }
    if (hasAiSubscription && aiRefundFilterValue) {
      payload[CALL_BINARY_FILTER_AI_REFUND] = aiRefundFilterValue;
    }

    const ivrSelectedDigitFilter = hasReverseIvrSubscription
      ? serializeMultiFilterValue(ivrSelectedDigitFilterValues)
      : undefined;
    if (ivrSelectedDigitFilter) {
      payload.ivr_selected_digit = ivrSelectedDigitFilter;
    }

    const participantFilter = hasTelephonyPack
      ? serializeMultiFilterValue(participantFilterValues)
      : undefined;
    if (participantFilter) {
      payload.participant_user = participantFilter;
    }

    const regionFilter = serializeMultiFilterValue(regionFilterValues);
    if (regionFilter) {
      payload.region = regionFilter;
    }

    const stateCodeFilter = serializeMultiFilterValue(stateCodeFilterValues);
    if (stateCodeFilter) {
      payload.state_code = stateCodeFilter;
    }

    const dddFilter = serializeMultiFilterValue(dddFilterValues);
    if (dddFilter) {
      payload.ddd = dddFilter;
    }

    const phoneTypeFilter = serializeMultiFilterValue(phoneTypeFilterValues);
    if (phoneTypeFilter) {
      payload.phone_type = phoneTypeFilter;
    }

    const carrierFilter = serializeMultiFilterValue(carrierFilterValues);
    if (carrierFilter) {
      payload.carrier = carrierFilter;
    }

    if (searchFilterValue) {
      payload.search = searchFilterValue;
    }
    if (resolvedDateParams.startDate) {
      payload.start_date = resolvedDateParams.startDate;
    }
    if (resolvedDateParams.endDate) {
      payload.end_date = resolvedDateParams.endDate;
    }
    return payload;
  }, [
    aiAnswerFilterValue,
    aiRefundFilterValue,
    aiAgentFilterValues,
    campaignFilterValue,
    carrierFilterValues,
    callTypeFilterValues,
    hasAiSubscription,
    hasReverseIvrSubscription,
    hasTelephonyPack,
    humanAnswerFilterValue,
    dddFilterValues,
    directionFilterValues,
    ivrSelectedDigitFilterValues,
    originFilterValues,
    outcomeFilterValues,
    participantFilterValues,
    phoneTypeFilterValues,
    regionFilterValues,
    resolvedDateParams.endDate,
    resolvedDateParams.startDate,
    searchFilterValue,
    serializeMultiFilterValue,
    showCallTypeFilter,
    stateCodeFilterValues,
    statusFilterValues,
    transferFilterValue,
  ]);
  const callReportFiltersPayload = useMemo<Record<string, string>>(
    () => ({
      ...serializedActiveFilters,
      ...(selectedDateRange ? { date_range: selectedDateRange } : {}),
    }),
    [selectedDateRange, serializedActiveFilters],
  );
  const callReportPeriodSummary = useMemo(() => {
    if (!resolvedDateParams.startDate || !resolvedDateParams.endDate) {
      return null;
    }

    const startValue = dayjs(resolvedDateParams.startDate);
    const endValue = dayjs(resolvedDateParams.endDate);
    if (!startValue.isValid() || !endValue.isValid()) {
      return `${resolvedDateParams.startDate} → ${resolvedDateParams.endDate}`;
    }
    return `${startValue.locale(dayjsLocale).format('L')} → ${endValue.locale(dayjsLocale).format('L')}`;
  }, [dayjsLocale, resolvedDateParams.endDate, resolvedDateParams.startDate]);
  const callReportSummaryEntries = useMemo<CallReportSummaryEntry[]>(() => {
    const entries: CallReportSummaryEntry[] = [
      {
        key: 'period',
        label: t('pages:calls.filters.dateRangeLabel'),
        values: [callReportPeriodSummary || t('pages:reports.calls.create.periodMissingDisplay')],
        warning: !callReportPeriodSummary ? t('pages:reports.calls.create.periodRequired') : null,
      },
    ];

    if (selectedCampaignOption?.label) {
      entries.push({
        key: 'campaign',
        label: t('pages:calls.filters.campaignLabel'),
        values: [selectedCampaignOption.label],
      });
    }

    const selectedOriginLabels = selectedOriginOptions.map((option) => option.label);
    if (selectedOriginLabels.length > 0) {
      entries.push({
        key: 'origin',
        label: t('pages:calls.filters.originLabel'),
        values: selectedOriginLabels,
      });
    }

    const selectedDirectionLabels = selectedDirectionOptions.map((option) => option.label);
    if (selectedDirectionLabels.length > 0) {
      entries.push({
        key: 'direction',
        label: t('pages:calls.direction.label'),
        values: selectedDirectionLabels,
      });
    }

    const selectedCallTypeLabels = selectedCallTypeOptions.map((option) => option.label);
    if (showCallTypeFilter && selectedCallTypeLabels.length > 0) {
      entries.push({
        key: 'call_type',
        label: t('pages:calls.filters.callTypeLabel'),
        values: selectedCallTypeLabels,
      });
    }

    const selectedAiAgentLabels = selectedAiAgentOptions.map((option) => option.label);
    if (hasAiSubscription && selectedAiAgentLabels.length > 0) {
      entries.push({
        key: 'ai_agent_id',
        label: t('pages:calls.filters.aiAgentLabel'),
        values: selectedAiAgentLabels,
      });
    }

    const selectedStatusLabels = resolveSelectedOptions(statusFilterValues, statusOptions).map((option) => option.label);
    if (selectedStatusLabels.length > 0) {
      entries.push({
        key: 'status',
        label: t('pages:calls.filters.statusLabel'),
        values: selectedStatusLabels,
      });
    }

    const selectedOutcomeLabels = resolveSelectedOptions(outcomeFilterValues, outcomeOptions).map((option) => option.label);
    if (selectedOutcomeLabels.length > 0) {
      entries.push({
        key: 'outcome',
        label: t('pages:calls.filters.outcomeLabel'),
        values: selectedOutcomeLabels,
      });
    }

    const booleanOptionMap = new Map(booleanFilterOptions.map((option) => [option.value, option.label]));
    const maybePushBooleanEntry = (key: string, label: string, value: string | null | undefined) => {
      if (!value) {
        return;
      }
      entries.push({
        key,
        label,
        values: [booleanOptionMap.get(value) ?? value],
      });
    };

    if (hasTelephonyPack) {
      maybePushBooleanEntry('has_transfer', t('pages:calls.filters.transferLabel'), transferFilterValue);
      maybePushBooleanEntry('has_human_answer', t('pages:calls.filters.humanAnswerLabel'), humanAnswerFilterValue);
    }
    if (hasAiSubscription) {
      maybePushBooleanEntry('has_ai_answer', t('pages:calls.filters.aiAnswerLabel'), aiAnswerFilterValue);
      maybePushBooleanEntry('has_ai_refund', t('pages:calls.filters.aiRefundLabel'), aiRefundFilterValue);
    }

    if (ivrSelectedDigitFilterValues.length > 0) {
      entries.push({
        key: 'ivr_selected_digit',
        label: t('pages:calls.filters.ivrSelectedDigitLabel'),
        values: ivrSelectedDigitFilterValues,
      });
    }

    const participantLabels = selectedParticipantOptions.map((option) => option.label);
    if (hasTelephonyPack && participantLabels.length > 0) {
      entries.push({
        key: 'participant_user',
        label: t('pages:calls.filters.participantLabel'),
        values: participantLabels,
      });
    }

    const regionLabels = selectedRegionOptions.map((option) => option.label);
    if (regionLabels.length > 0) {
      entries.push({
        key: 'region',
        label: t('pages:calls.filters.regionLabel'),
        values: regionLabels,
      });
    }

    const stateCodeLabels = selectedStateCodeOptions.map((option) => option.label);
    if (stateCodeLabels.length > 0) {
      entries.push({
        key: 'state_code',
        label: t('pages:calls.filters.stateLabel'),
        values: stateCodeLabels,
      });
    }

    const dddLabels = selectedDddOptions.map((option) => option.label);
    if (dddLabels.length > 0) {
      entries.push({
        key: 'ddd',
        label: t('pages:calls.filters.dddLabel'),
        values: dddLabels,
      });
    }

    const phoneTypeLabels = selectedPhoneTypeOptions.map((option) => option.label);
    if (phoneTypeLabels.length > 0) {
      entries.push({
        key: 'phone_type',
        label: t('pages:calls.filters.phoneTypeLabel'),
        values: phoneTypeLabels,
      });
    }

    const carrierLabels = selectedCarrierOptions.map((option) => option.label);
    if (carrierLabels.length > 0) {
      entries.push({
        key: 'carrier',
        label: t('pages:calls.filters.carrierLabel'),
        values: carrierLabels,
      });
    }

    if (searchFilterValue) {
      entries.push({
        key: 'search',
        label: t('pages:reports.calls.create.searchFilterLabel'),
        values: [searchFilterValue],
      });
    }

    return entries;
  }, [
    aiAnswerFilterValue,
    aiRefundFilterValue,
    booleanFilterOptions,
    callReportPeriodSummary,
    hasAiSubscription,
    hasTelephonyPack,
    humanAnswerFilterValue,
    ivrSelectedDigitFilterValues,
    outcomeFilterValues,
    outcomeOptions,
    resolveSelectedOptions,
    searchFilterValue,
    selectedAiAgentOptions,
    selectedCampaignOption,
    selectedCarrierOptions,
    selectedCallTypeOptions,
    selectedDddOptions,
    selectedDirectionOptions,
    selectedOriginOptions,
    selectedParticipantOptions,
    selectedPhoneTypeOptions,
    selectedRegionOptions,
    selectedStateCodeOptions,
    statusFilterValues,
    statusOptions,
    t,
    showCallTypeFilter,
    transferFilterValue,
  ]);
  const callReportValidationMessage = useMemo(() => {
    if (!resolvedDateParams.startDate || !resolvedDateParams.endDate) {
      return t('pages:reports.calls.create.periodRequired');
    }
    const startValue = dayjs(resolvedDateParams.startDate);
    const endValue = dayjs(resolvedDateParams.endDate);
    if (!startValue.isValid() || !endValue.isValid()) {
      return t('pages:reports.calls.create.periodRequired');
    }
    if (endValue.endOf('day').diff(startValue.startOf('day')) > 7 * 24 * 60 * 60 * 1000) {
      return t('pages:reports.calls.create.periodTooLarge');
    }
    return null;
  }, [resolvedDateParams.endDate, resolvedDateParams.startDate, t]);
  const defaultCallReportName = useMemo(() => {
    const nameParts = [t('pages:reports.calls.create.defaultName')];
    if (selectedCampaignOption?.label) {
      nameParts.push(selectedCampaignOption.label);
    } else {
      const firstStatusLabel = resolveSelectedOptions(statusFilterValues, statusOptions)[0]?.label;
      if (firstStatusLabel) {
        nameParts.push(firstStatusLabel);
      }
    }
    if (callReportPeriodSummary) {
      nameParts.push(callReportPeriodSummary);
    }
    return nameParts.join(' • ');
  }, [callReportPeriodSummary, resolveSelectedOptions, selectedCampaignOption, statusFilterValues, statusOptions, t]);
  const defaultCallReportDescription = useMemo(
    () =>
      callReportSummaryEntries
        .map((entry) => `${entry.label}: ${entry.values.join(', ')}`)
        .join(' | '),
    [callReportSummaryEntries],
  );

  const loadCampaignOptions = useCallback(async (searchTerm: string) => {
    const requestId = campaignRequestIdRef.current + 1;
    campaignRequestIdRef.current = requestId;
    setIsCampaignLoading(true);
    try {
      const response = await api.get('/api/calls/campaign-options/', {
        params: {
          search: searchTerm || undefined,
          limit: CAMPAIGN_AUTOCOMPLETE_LIMIT,
        },
      });
      if (campaignRequestIdRef.current !== requestId) {
        return;
      }

      const rawOptions: CallCampaignApiOption[] = Array.isArray(response.data) ? response.data : [];
      const nextOptions: CallFilterOption[] = rawOptions.map((item) => ({
        value: item.id,
        label: item.name || item.id,
      }));
      setCampaignOptions(sortOptionsByLabel(nextOptions));
    } catch (error) {
      if (campaignRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading call campaign filter options:', error);
      setCampaignOptions([]);
    } finally {
      if (campaignRequestIdRef.current === requestId) {
        setIsCampaignLoading(false);
      }
    }
  }, [sortOptionsByLabel]);

  const loadAiAgentOptions = useCallback(async (searchTerm: string) => {
    const requestId = aiAgentRequestIdRef.current + 1;
    aiAgentRequestIdRef.current = requestId;
    setIsAiAgentLoading(true);
    try {
      const response = await api.get('/api/calls/ai-agent-options/', {
        params: {
          search: searchTerm || undefined,
          limit: AI_AGENT_AUTOCOMPLETE_LIMIT,
        },
      });
      if (aiAgentRequestIdRef.current !== requestId) {
        return;
      }

      const rawOptions: CallAiAgentApiOption[] = Array.isArray(response.data) ? response.data : [];
      const nextOptions: CallFilterOption[] = rawOptions.map((item) => ({
        value: item.id,
        label: item.name || item.id,
      }));
      setAiAgentOptions(sortOptionsByLabel(nextOptions));
    } catch (error) {
      if (aiAgentRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading call AI agent filter options:', error);
      setAiAgentOptions([]);
    } finally {
      if (aiAgentRequestIdRef.current === requestId) {
        setIsAiAgentLoading(false);
      }
    }
  }, [sortOptionsByLabel]);

  const loadParticipantOptions = useCallback(async (searchTerm: string) => {
    const requestId = participantRequestIdRef.current + 1;
    participantRequestIdRef.current = requestId;
    setIsParticipantLoading(true);
    try {
      const response = await api.get('/api/calls/participant-options/', {
        params: {
          search: searchTerm || undefined,
          limit: PARTICIPANT_AUTOCOMPLETE_LIMIT,
        },
      });
      if (participantRequestIdRef.current !== requestId) {
        return;
      }

      const rawOptions: CallParticipantApiOption[] = Array.isArray(response.data) ? response.data : [];
      const nextOptions: CallFilterOption[] = rawOptions.map((item) => ({
        value: item.id,
        label: item.full_name || item.fullName || item.username || item.email || item.id,
      }));
      setParticipantOptions(sortOptionsByLabel(nextOptions));
    } catch (error) {
      if (participantRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error loading call participant filter options:', error);
      setParticipantOptions([]);
    } finally {
      if (participantRequestIdRef.current === requestId) {
        setIsParticipantLoading(false);
      }
    }
  }, [sortOptionsByLabel]);

  const loadGeoFilterOptions = useCallback(async () => {
    setIsGeoOptionsLoading(true);
    try {
      const response = await api.get('/api/calls/geo-filter-options/', {
        params: serializedActiveFilters,
      });
      const rawOptions = (response.data || {}) as CallGeoFilterOptionsResponse;
      const nextRegionOptions = sortOptionsByLabel(
        Array.isArray(rawOptions.regions)
          ? rawOptions.regions.map((item) => buildRegionOption(item.value))
          : [],
      );
      const nextStateCodeOptions = sortOptionsByLabel(
        Array.isArray(rawOptions.states)
          ? rawOptions.states.map((item) => ({
              value: item.value,
              label: item.label || item.value,
            }))
          : [],
      );
      const nextDddOptions = sortDddOptions(
        Array.isArray(rawOptions.ddds)
          ? rawOptions.ddds.map((item) => ({ value: item.value, label: item.value }))
          : [],
      );
      setRegionOptions(nextRegionOptions);
      setStateCodeOptions(nextStateCodeOptions);
      setDddOptions(nextDddOptions);
    } catch (error) {
      console.error('Error loading call geo filter options:', error);
      setRegionOptions([]);
      setStateCodeOptions([]);
      setDddOptions([]);
    } finally {
      setIsGeoOptionsLoading(false);
    }
  }, [buildRegionOption, serializedActiveFilters, sortDddOptions, sortOptionsByLabel]);

  const loadCarrierFilterOptions = useCallback(async () => {
    setIsCarrierOptionsLoading(true);
    try {
      const response = await api.get('/api/calls/carrier-filter-options/', {
        params: serializedActiveFilters,
      });
      const rawOptions = (response.data || {}) as CallCarrierFilterOptionsResponse;
      const nextCarrierOptions = sortOptionsByLabel(
        Array.isArray(rawOptions.carriers)
          ? rawOptions.carriers.map((item) => ({
              value: item.value,
              label: item.value === 'unknown'
                ? t('pages:calls.filters.carrierUnknown')
                : item.label || item.value,
            }))
          : [],
      );
      setCarrierOptions(nextCarrierOptions);
    } catch (error) {
      console.error('Error loading call carrier filter options:', error);
      setCarrierOptions([]);
    } finally {
      setIsCarrierOptionsLoading(false);
    }
  }, [serializedActiveFilters, sortOptionsByLabel, t]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
        ...serializedActiveFilters,
      };

      const response = await api.get('/api/calls/', { params });
      const data = response.data.results ?? response.data;
      setCalls(Array.isArray(data) ? data : []);
      setTotalCalls(response.data.count ?? data.length ?? 0);
    } catch (error) {
      console.error('Error loading calls:', error);
      setCalls([]);
      setTotalCalls(0);
    } finally {
      setLoading(false);
    }
  }, [
    orderingParam,
    page,
    rowsPerPage,
    serializedActiveFilters,
  ]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  useEffect(() => {
    void loadGeoFilterOptions();
  }, [loadGeoFilterOptions]);

  useEffect(() => {
    void loadCarrierFilterOptions();
  }, [loadCarrierFilterOptions]);

  useEffect(() => {
    setSearchInput(searchFilterValue);
  }, [searchFilterValue]);

  useEffect(() => {
    if (hasAiSubscription) {
      return;
    }
    const nextParams: Record<string, null> = {};
    let changed = false;
    if (filters.has_ai_answer) {
      nextParams[CALL_BINARY_FILTER_AI_ANSWER] = null;
      changed = true;
    }
    if (filters.has_ai_refund) {
      nextParams[CALL_BINARY_FILTER_AI_REFUND] = null;
      changed = true;
    }
    if (filters.ai_agent_id) {
      nextParams.ai_agent_id = null;
      changed = true;
    }
    if (!changed) {
      return;
    }
    setParams(nextParams, { resetPage: true });
  }, [filters.ai_agent_id, filters.has_ai_answer, filters.has_ai_refund, hasAiSubscription, setParams]);

  useEffect(() => {
    if (showCallTypeFilter) {
      return;
    }
    if (!filters.call_type) {
      return;
    }
    setParams({ call_type: null }, { resetPage: true });
  }, [filters.call_type, setParams, showCallTypeFilter]);

  useEffect(() => {
    if (!showCallTypeFilter || callTypeFilterValues.length === 0) {
      return;
    }
    const nextValues = callTypeFilterValues.filter((value) => callTypeOptionValues.has(value));
    if (nextValues.length === callTypeFilterValues.length) {
      return;
    }
    setParams(
      { call_type: serializeMultiFilterValue(nextValues) ?? null },
      { resetPage: true },
    );
  }, [
    callTypeFilterValues,
    callTypeOptionValues,
    serializeMultiFilterValue,
    setParams,
    showCallTypeFilter,
  ]);

  useEffect(() => {
    if (hasTelephonyPack) {
      return;
    }
    const nextParams: Record<string, null> = {};
    let changed = false;
    if (filters.has_transfer) {
      nextParams.has_transfer = null;
      changed = true;
    }
    if (filters.has_human_answer) {
      nextParams.has_human_answer = null;
      changed = true;
    }
    if (filters.participant_user) {
      nextParams.participant_user = null;
      changed = true;
    }
    if (!changed) {
      return;
    }
    setParams(nextParams, { resetPage: true });
  }, [
    filters.has_human_answer,
    filters.has_transfer,
    filters.participant_user,
    hasTelephonyPack,
    setParams,
  ]);

  useEffect(() => {
    if (hasReverseIvrSubscription) {
      return;
    }
    const nextOutcomeValues = outcomeFilterValues.filter((value) => value !== 'ivr_heard_to_end');
    const nextParams: Record<string, string | null> = {};
    let changed = false;
    if (nextOutcomeValues.length !== outcomeFilterValues.length) {
      nextParams.outcome = serializeMultiFilterValue(nextOutcomeValues) ?? null;
      changed = true;
    }
    if (filters.ivr_selected_digit) {
      nextParams.ivr_selected_digit = null;
      changed = true;
    }
    if (!changed) {
      return;
    }
    setParams(nextParams, { resetPage: true });
  }, [
    filters.ivr_selected_digit,
    hasReverseIvrSubscription,
    outcomeFilterValues,
    serializeMultiFilterValue,
    setParams,
  ]);

  useEffect(() => {
    if (hasTelephonyPack) {
      return;
    }
    setParticipantOptions([]);
    setParticipantSearchInput('');
    setIsParticipantOpen(false);
  }, [hasTelephonyPack]);

  useEffect(() => {
    if (hasAiSubscription) {
      return;
    }
    setAiAgentOptions([]);
    setAiAgentSearchInput('');
    setIsAiAgentOpen(false);
  }, [hasAiSubscription]);

  useEffect(() => {
    if (!shouldLoadCampaignOptions) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void loadCampaignOptions(campaignSearchInput);
    }, CAMPAIGN_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [campaignSearchInput, loadCampaignOptions, shouldLoadCampaignOptions]);

  useEffect(() => {
    if (!shouldLoadAiAgentOptions) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void loadAiAgentOptions(aiAgentSearchInput);
    }, AI_AGENT_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [aiAgentSearchInput, loadAiAgentOptions, shouldLoadAiAgentOptions]);

  useEffect(() => {
    if (!shouldLoadParticipantOptions) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void loadParticipantOptions(participantSearchInput);
    }, PARTICIPANT_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadParticipantOptions, participantSearchInput, shouldLoadParticipantOptions]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const normalizedSearch = searchInput.trim();
      if (normalizedSearch.length > 0 && normalizedSearch.length < SEARCH_MIN_CHARACTERS) {
        return;
      }

      const nextSearchParam = normalizedSearch.length === 0 ? undefined : normalizedSearch;
      const currentSearchParam = searchFilterValue || undefined;
      if (currentSearchParam === nextSearchParam) {
        return;
      }

      setParams({ search: nextSearchParam }, { resetPage: true });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchFilterValue, searchInput, setParams]);

  useEffect(() => {
    if (!selectedCallId) {
      return;
    }

    const scrollToRow = () => {
      const rowElement = callRowRefs.current[selectedCallId];
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const primaryTimeout = window.setTimeout(scrollToRow, 100);
    const retryTimeout = window.setTimeout(scrollToRow, 600);
    return () => {
      window.clearTimeout(primaryTimeout);
      window.clearTimeout(retryTimeout);
    };
  }, [selectedCallId]);

  const renderCallType = (call: CallListItem) => {
    const resolvedCallType = call.callType ?? 'sip';
    const label = t(`pages:calls.callTypeLabels.${resolvedCallType}`);
    const iconSx = { fontSize: 16, color: theme.palette.text.secondary };

    const icon =
      resolvedCallType === 'ai_agent' ? (
        <PsychologyIcon sx={iconSx} />
      ) : resolvedCallType === 'reverse_ivr' ? (
        <Dialpad sx={iconSx} />
      ) : (
        <Phone sx={iconSx} />
      );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon}
        <Typography
          fontWeight={600}
          variant="body2"
          sx={{ whiteSpace: 'nowrap', lineHeight: 1.35 }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const renderStatusFilterGroupHeader = (group: CallStatusGroup) => (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: theme.palette.background.paper,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {t(`pages:calls.filters.statusGroups.${group}.label`)}
      </Typography>
      <Tooltip title={t(`pages:calls.filters.statusGroups.${group}.help`)} arrow>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );

  const renderReverseIvrDigit = (call: CallListItem) => {
    const value = String(call.ivrSelectedDigit || '').trim();
    return value || '—';
  };

  const renderCreatedByOrigin = (
    call: CallListItem,
    {
      includeExtension = false,
    }: {
      includeExtension?: boolean;
    } = {},
  ) => {
    const userName = call.createdByName?.trim();
    const extension = call.createdByExtension?.trim();
    const isManualSipDirect = call.origin === 'manual' && call.callType === 'sip';
    const isManualAiAgent = call.origin === 'manual' && call.callType === 'ai_agent';

    if (!userName && (!includeExtension || !extension)) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {call.callType === 'ai_agent' && call.agentName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PsychologyIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography
              fontWeight={600}
              sx={{
                fontSize: isManualAiAgent ? '0.9375rem' : undefined,
                lineHeight: 1.35,
              }}
            >
              {call.agentName}
            </Typography>
          </Box>
        )}
        {userName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography
              fontWeight={isManualAiAgent ? undefined : 600}
              variant={isManualAiAgent ? 'caption' : 'body1'}
              color={isManualAiAgent ? 'text.secondary' : undefined}
              sx={{
                fontSize: isManualSipDirect ? '0.9375rem' : undefined,
                lineHeight: 1.35,
              }}
            >
              {userName}
            </Typography>
          </Box>
        )}
        {includeExtension && extension && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Phone sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography variant="caption" color="text.secondary">
              {extension}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderOriginDetails = (call: CallListItem) => {
    const iconSx = { fontSize: 16, color: theme.palette.text.secondary };
    const isCampaignCall = call.origin === 'campaign' && Boolean(call.campaignId);
    const callerNumber = String(call.fromNumber || '').trim();
    const inboundRouteName = String(call.originLineName || '').trim();

    const renderCampaignOrigin = (
      includeAgent = false,
    ) => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Campaign sx={iconSx} />
          <Typography fontWeight={600}>
            {call.campaignName || t('pages:calls.campaign')}
          </Typography>
        </Box>
        {includeAgent && call.agentName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PsychologyIcon sx={iconSx} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              {call.agentName}
            </Typography>
          </Box>
        )}
      </Box>
    );

    if (isCampaignCall && call.callType === 'reverse_ivr') {
      return renderCampaignOrigin();
    }

    if (isCampaignCall && call.callType === 'ai_agent') {
      return renderCampaignOrigin(true);
    }

    if (call.origin === 'manual' && call.callType === 'reverse_ivr') {
      return renderCreatedByOrigin(call);
    }

    if (call.origin === 'manual' && call.callType === 'ai_agent') {
      return renderCreatedByOrigin(call);
    }

    if (call.origin === 'inbound_route' || call.direction === 'inbound') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CallReceived sx={iconSx} />
            <Typography fontWeight={600} sx={{ lineHeight: 1.35 }}>
              {callerNumber ? formatPhone(callerNumber) : t('pages:calls.origin.inbound_route')}
            </Typography>
          </Box>
          {inboundRouteName && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
              {inboundRouteName}
            </Typography>
          )}
        </Box>
      );
    }

    if (call.callType === 'sip') {
      return renderCreatedByOrigin(call, { includeExtension: true });
    }

    return null;
  };

  const renderDestinationDetails = (call: CallListItem) => {
    const showContactDetails = call.direction === 'outbound' && Boolean(call.campaignContactId);
    const phoneLabel = call.phoneLabel?.trim();

    if (!showContactDetails) {
      return (
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
          {formatPhone(call.toNumber)}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {call.campaignContactDisplayName && (
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
            {call.campaignContactDisplayName}
          </Typography>
        )}
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
          {formatPhone(call.toNumber)}
        </Typography>
        {phoneLabel && (
          <Typography variant="caption" color="text.secondary">
            {phoneLabel}
          </Typography>
        )}
      </Box>
    );
  };

  const getCallDetailLink = (call: CallListItem) => {
    const detailParams = new URLSearchParams(searchParams);
    detailParams.set('call', call.id);
    const queryString = detailParams.toString();
    return `/calls${queryString ? `?${queryString}` : ''}`;
  };

  const renderStatusChip = (call: CallListItem) => (
    <CallStatusChip
      status={call.status as CallStatus}
      isConverted={call.isConverted}
      isOptout={call.isOptout}
      isMismatch={call.isMismatch}
      sx={{ minWidth: 100 }}
    />
  );

  const formatStartTime = (call: CallListItem) => {
    const timestamp = call.initiatedAt || call.createdAt;
    if (!timestamp) return '-';
    return dayjs(timestamp).locale(dayjsLocale).format('L [às] HH:mm');
  };

  const handleCallReportCreated = useCallback(() => {
    setIsCreateCallReportOpen(false);
    navigate('/reports?tab=calls');
  }, [navigate]);

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {canCreateCallReport && (
        <ListActionButton
          variant="outlined"
          startIcon={<Assessment />}
          onClick={() => setIsCreateCallReportOpen(true)}
        >
          {t('pages:reports.calls.create.openButton')}
        </ListActionButton>
      )}
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={fetchCalls}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
    </Box>
  );

  const filterControls = (
    <>
      <Autocomplete<CallFilterOption, false, false, false>
        options={campaignOptions}
        loading={isCampaignLoading}
        value={selectedCampaignOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onOpen={() => {
          setIsCampaignOpen(true);
        }}
        onClose={() => {
          setIsCampaignOpen(false);
        }}
        onInputChange={(_, nextValue, reason) => {
          if (reason === 'reset') {
            return;
          }
          setCampaignSearchInput(nextValue);
        }}
        onChange={(_, selectedOption) => {
          setParams({ campaign: selectedOption?.value ?? null }, { resetPage: true });
        }}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_CAMPAIGN)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.campaignLabel')}
            placeholder={t('pages:calls.filters.campaignSearchPlaceholder')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={originOptions}
        value={selectedOriginOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ origin: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_ORIGIN)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.originLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={directionOptions}
        value={selectedDirectionOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ direction: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_DIRECTION)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.direction.label')}
            sx={filterInputSx}
          />
        )}
      />
      {showCallTypeFilter && (
        <Autocomplete<CallFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={callTypeOptions}
          value={selectedCallTypeOptions}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOptions) => {
            const nextValues = selectedOptions.map((option) => option.value);
            setParams({ call_type: serializeMultiFilterValue(nextValues) }, { resetPage: true });
          }}
          renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_CALL_TYPE)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.callTypeLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasAiSubscription && (
        <Autocomplete<CallFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={aiAgentOptions}
          loading={isAiAgentLoading}
          value={selectedAiAgentOptions}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onOpen={() => {
            setIsAiAgentOpen(true);
          }}
          onClose={() => {
            setIsAiAgentOpen(false);
          }}
          onInputChange={(_, nextValue, reason) => {
            if (reason === 'reset') {
              return;
            }
            setAiAgentSearchInput(nextValue);
          }}
          onChange={(_, selectedOptions) => {
            const nextValues = selectedOptions.map((option) => option.value);
            setParams({ ai_agent_id: serializeMultiFilterValue(nextValues) }, { resetPage: true });
          }}
          renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_AI_AGENT)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.aiAgentLabel')}
              placeholder={t('pages:calls.filters.aiAgentSearchPlaceholder')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      <Autocomplete<CallStatusFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={statusOptions}
        value={resolveSelectedOptions(statusFilterValues, statusOptions)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        groupBy={(option) => option.group}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ status: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderGroup={(params) => (
          <li key={params.key}>
            {renderStatusFilterGroupHeader(params.group as CallStatusGroup)}
            <Box component="ul" sx={{ p: 0, m: 0 }}>
              {params.children}
            </Box>
          </li>
        )}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_STATUS)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.statusLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={outcomeOptions}
        value={resolveSelectedOptions(outcomeFilterValues, outcomeOptions)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ outcome: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_OUTCOME)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.outcomeLabel')}
            sx={filterInputSx}
          />
        )}
      />
      {hasTelephonyPack && (
        <Autocomplete<CallFilterOption, false, false, false>
          options={booleanFilterOptions}
          value={resolveSingleSelectedOption(transferFilterValue, booleanFilterOptions)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOption) => {
            setParams({ has_transfer: selectedOption?.value ?? null }, { resetPage: true });
          }}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_BOOLEAN)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.transferLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasTelephonyPack && (
        <Autocomplete<CallFilterOption, false, false, false>
          options={booleanFilterOptions}
          value={resolveSingleSelectedOption(humanAnswerFilterValue, booleanFilterOptions)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOption) => {
            setParams({ has_human_answer: selectedOption?.value ?? null }, { resetPage: true });
          }}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_BOOLEAN)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.humanAnswerLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasAiSubscription && (
        <Autocomplete<CallFilterOption, false, false, false>
          options={booleanFilterOptions}
          value={resolveSingleSelectedOption(aiAnswerFilterValue, booleanFilterOptions)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOption) => {
            setParams({ [CALL_BINARY_FILTER_AI_ANSWER]: selectedOption?.value ?? null }, { resetPage: true });
          }}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_AI_ANSWER)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.aiAnswerLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasAiSubscription && (
        <Autocomplete<CallFilterOption, false, false, false>
          options={booleanFilterOptions}
          value={resolveSingleSelectedOption(aiRefundFilterValue, booleanFilterOptions)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOption) => {
            setParams({ [CALL_BINARY_FILTER_AI_REFUND]: selectedOption?.value ?? null }, { resetPage: true });
          }}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_BOOLEAN)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.aiRefundLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasReverseIvrSubscription && (
        <Autocomplete<CallFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={reverseIvrDigitOptions}
          value={resolveSelectedOptions(ivrSelectedDigitFilterValues, reverseIvrDigitOptions)}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, selectedOptions) => {
            const nextValues = selectedOptions.map((option) => option.value);
            setParams({ ivr_selected_digit: serializeMultiFilterValue(nextValues) }, { resetPage: true });
          }}
          renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_DTMF)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.ivrSelectedDigitLabel')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      {hasTelephonyPack && (
        <Autocomplete<CallFilterOption, true, false, false>
          multiple
          disableCloseOnSelect
          options={participantOptions}
          loading={isParticipantLoading}
          value={selectedParticipantOptions}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onOpen={() => {
            setIsParticipantOpen(true);
          }}
          onClose={() => {
            setIsParticipantOpen(false);
          }}
          onInputChange={(_, nextValue, reason) => {
            if (reason === 'reset') {
              return;
            }
            setParticipantSearchInput(nextValue);
          }}
          onChange={(_, selectedOptions) => {
            const nextValues = selectedOptions.map((option) => option.value);
            setParams({ participant_user: serializeMultiFilterValue(nextValues) }, { resetPage: true });
          }}
          renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
          sx={buildFilterFieldSx(FILTER_MIN_WIDTH_PARTICIPANT)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('pages:calls.filters.participantLabel')}
              placeholder={t('pages:calls.filters.participantSearchPlaceholder')}
              sx={filterInputSx}
            />
          )}
        />
      )}
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={regionOptions}
        loading={isGeoOptionsLoading}
        value={selectedRegionOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ region: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_GEO)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.regionLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={stateCodeOptions}
        loading={isGeoOptionsLoading}
        value={selectedStateCodeOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ state_code: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_GEO)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.stateLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={dddOptions}
        loading={isGeoOptionsLoading}
        value={selectedDddOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ ddd: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_GEO)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.dddLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={phoneTypeOptions}
        value={selectedPhoneTypeOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ phone_type: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_PHONE_TYPE)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.phoneTypeLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallFilterOption, true, false, false>
        multiple
        disableCloseOnSelect
        options={carrierOptions}
        loading={isCarrierOptionsLoading}
        value={selectedCarrierOptions}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOptions) => {
          const nextValues = selectedOptions.map((option) => option.value);
          setParams({ carrier: serializeMultiFilterValue(nextValues) }, { resetPage: true });
        }}
        renderOption={(props, option, { selected }) => renderCheckboxFilterOption(props, option.label, selected)}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_CARRIER)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.carrierLabel')}
            sx={filterInputSx}
          />
        )}
      />
      <Autocomplete<CallDateRangeOption, false, false, false>
        options={dateRangeOptions}
        value={dateRangeOption}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, selectedOption) => {
          const nextDateRange = selectedOption?.value ?? null;
          if (!nextDateRange) {
            setParams({ date_range: null, start_date: null, end_date: null }, { resetPage: true });
            return;
          }

          if (nextDateRange === 'period') {
            setParams({ date_range: nextDateRange }, { resetPage: true });
            return;
          }

          setParams({ date_range: nextDateRange, start_date: null, end_date: null }, { resetPage: true });
        }}
        sx={buildFilterFieldSx(FILTER_MIN_WIDTH_DATE_RANGE)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('pages:calls.filters.dateRangeLabel')}
            sx={filterInputSx}
          />
        )}
      />
      {selectedDateRange === 'period' && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <>
            <DateTimePicker
              label={t('pages:calls.filters.fromDate')}
              value={startDateValue}
              onChange={(value) => {
                const formattedDate = value ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : undefined;
                setParams({ start_date: formattedDate }, { resetPage: true });
              }}
              maxDate={endDateValue ?? undefined}
              slotProps={{
                textField: { size: 'small', sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' } },
              }}
            />
            <DateTimePicker
              label={t('pages:calls.filters.toDate')}
              value={endDateValue}
              onChange={(value) => {
                const formattedDate = value ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : undefined;
                setParams({ end_date: formattedDate }, { resetPage: true });
              }}
              minDate={startDateValue ?? undefined}
              slotProps={{
                textField: { size: 'small', sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' } },
              }}
            />
          </>
        </LocalizationProvider>
      )}
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:calls.filters.searchPlaceholder')}
        value={searchInput}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearchInput(event.target.value);
        }}
        sx={{ ...filterInputSx, ...buildFilterFieldSx(FILTER_MIN_WIDTH_SEARCH) }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<Clear />}
        disabled={
          !searchFilterValue &&
          !searchInput.trim() &&
          !campaignFilterValue &&
          originFilterValues.length === 0 &&
          directionFilterValues.length === 0 &&
          callTypeFilterValues.length === 0 &&
          aiAgentFilterValues.length === 0 &&
          statusFilterValues.length === 0 &&
          outcomeFilterValues.length === 0 &&
          !transferFilterValue &&
          !humanAnswerFilterValue &&
          !aiAnswerFilterValue &&
          !aiRefundFilterValue &&
          ivrSelectedDigitFilterValues.length === 0 &&
          participantFilterValues.length === 0 &&
          regionFilterValues.length === 0 &&
          stateCodeFilterValues.length === 0 &&
          dddFilterValues.length === 0 &&
          phoneTypeFilterValues.length === 0 &&
          carrierFilterValues.length === 0 &&
          !selectedDateRange &&
          !startDateFilterValue &&
          !endDateFilterValue
        }
        onClick={() => {
          if (
            !searchFilterValue &&
            !searchInput.trim() &&
            !campaignFilterValue &&
            originFilterValues.length === 0 &&
            directionFilterValues.length === 0 &&
            callTypeFilterValues.length === 0 &&
            aiAgentFilterValues.length === 0 &&
            statusFilterValues.length === 0 &&
            outcomeFilterValues.length === 0 &&
            !transferFilterValue &&
            !humanAnswerFilterValue &&
            !aiAnswerFilterValue &&
            !aiRefundFilterValue &&
            ivrSelectedDigitFilterValues.length === 0 &&
            participantFilterValues.length === 0 &&
            regionFilterValues.length === 0 &&
            stateCodeFilterValues.length === 0 &&
            dddFilterValues.length === 0 &&
            phoneTypeFilterValues.length === 0 &&
            carrierFilterValues.length === 0 &&
            !selectedDateRange &&
            !startDateFilterValue &&
            !endDateFilterValue
          )
            return;
          setParams(
            { campaign: null, origin: null, direction: null, call_type: null, ai_agent_id: null, search: null, status: null, outcome: null, has_transfer: null, has_human_answer: null, has_ai_answer: null, has_ai_refund: null, ivr_selected_digit: null, participant_user: null, region: null, state_code: null, ddd: null, phone_type: null, carrier: null, date_range: null, start_date: null, end_date: null },
            { resetPage: true },
          );
          setSearchInput('');
          setCampaignSearchInput('');
          setAiAgentSearchInput('');
          setParticipantSearchInput('');
        }}
        sx={{
          textTransform: 'none',
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const tableContent = isMobile ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      {loading
        ? [...Array(3)].map((_, index) => (
            <Card
              key={index}
              sx={{
                minHeight: 160,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow: mobileCardShadow,
              }}
            >
              <Box sx={{ p: 2 }}>
                <Skeleton height={24} width="60%" />
                <Skeleton height={18} sx={{ mt: 1, width: '45%' }} />
                <Skeleton height={32} sx={{ mt: 2 }} />
              </Box>
            </Card>
          ))
        : calls.map((call) => {
            const detailLink = getCallDetailLink(call);
            return (
              <Card
                component={RouterLink}
                to={detailLink}
                key={call.id}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: mobileCardShadow,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  {showCallTypeColumn && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:calls.callType')}
                      </Typography>
                      {renderCallType(call)}
                    </>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {t('pages:calls.from')}
                  </Typography>
                  {renderOriginDetails(call)}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('pages:calls.toNumber')}
                  </Typography>
                  {renderDestinationDetails(call)}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    px: 2,
                    pb: 2,
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:calls.startTime')}
                    </Typography>
                    <Typography fontWeight={600}>{formatStartTime(call)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages:calls.duration')}
                    </Typography>
                    <Typography fontWeight={600}>{formatCallDurationValue(call)}</Typography>
                  </Box>
                  {showConsumptionColumn && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:calls.consumption')}
                      </Typography>
                      <CallConsumptionDisplay
                        segments={call.consumptionSegments}
                        activeSegmentId={call.activeSegmentId}
                        activeSegmentType={call.activeSegmentType}
                        callStatus={call.status}
                        fallbackBilledSeconds={getBilledSeconds(call)}
                        fallbackDurationSeconds={getEffectiveSeconds(call)}
                        fallbackBucketType={call.callType === 'ai_agent' ? 'ai' : 'telephony'}
                        compact
                      />
                    </Box>
                  )}
                  {showReverseIvrDigitColumn && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:calls.filters.ivrSelectedDigitLabel')}
                      </Typography>
                      <Typography fontWeight={600}>{renderReverseIvrDigit(call)}</Typography>
                    </Box>
                  )}
                  <Box>{renderStatusChip(call)}</Box>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    mx: 2,
                    mb: 2,
                    overflow: 'hidden',
                    boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(100, (getBilledSeconds(call) / 60) * 2)}%`,
                      backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }}
                  />
                </Box>
              </Card>
            );
          })}
    </Box>
  ) : (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            },
            '& .MuiTableSortLabel-root': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          {showCallTypeColumn && (
            <TableCell>{columnTitle(t('pages:calls.callType'))}</TableCell>
          )}
          <TableCell>{columnTitle(t('pages:calls.phoneNumber'))}</TableCell>
          <TableCell>{columnTitle(t('pages:calls.toNumber'))}</TableCell>
          <TableCell sortDirection={isStartTimeOrdering ? activeOrderingDirection : false}>
            <TableSortLabel
              active={isStartTimeOrdering}
              direction={isStartTimeOrdering ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange('activity_at', START_TIME_ORDERING_FIELDS)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {columnTitle(t('pages:calls.startTime'))}
            </TableSortLabel>
          </TableCell>
          <TableCell>{columnTitle(t('pages:calls.duration'))}</TableCell>
          {showConsumptionColumn && (
            <TableCell
              sortDirection={activeOrderingField === 'consumption_seconds' ? activeOrderingDirection : false}
            >
              <TableSortLabel
                active={activeOrderingField === 'consumption_seconds'}
                direction={activeOrderingField === 'consumption_seconds' ? activeOrderingDirection : 'asc'}
                onClick={() => handleOrderingChange('consumption_seconds')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {columnTitle(t('pages:calls.consumption'))}
              </TableSortLabel>
            </TableCell>
          )}
          {showReverseIvrDigitColumn && (
            <TableCell>{columnTitle(t('pages:calls.filters.ivrSelectedDigitLabel'))}</TableCell>
          )}
          <TableCell>{columnTitle(t('pages:calls.status'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
          {loading ? (
            [...Array(rowsPerPage)].map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                {[...Array(
                  5
                  + (showCallTypeColumn ? 1 : 0)
                  + (showConsumptionColumn ? 1 : 0)
                  + (showReverseIvrDigitColumn ? 1 : 0)
                )].map((_, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : calls.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={
                5
                + (showCallTypeColumn ? 1 : 0)
                + (showConsumptionColumn ? 1 : 0)
                + (showReverseIvrDigitColumn ? 1 : 0)
              }
              align="center"
              sx={{ py: 8 }}
            >
              <Typography color="text.secondary">{t('pages:calls.noCalls')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          calls.map((call) => {
            const detailLink = getCallDetailLink(call);
            return (
              <TableRow
                key={call.id}
                hover
                ref={(element) => {
                  if (element) {
                    callRowRefs.current[call.id] = element;
                  } else {
                    delete callRowRefs.current[call.id];
                  }
                }}
                sx={{
                  transition: 'background 0.2s',
                  backgroundColor:
                    selectedCallId === call.id ? alpha(theme.palette.primary.main, 0.12) : undefined,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                {showCallTypeColumn && (
                  <LinkTableCell to={detailLink}>
                    {renderCallType(call)}
                  </LinkTableCell>
                )}
                <LinkTableCell to={detailLink}>
                  {renderOriginDetails(call)}
                </LinkTableCell>
                <LinkTableCell to={detailLink}>
                  {renderDestinationDetails(call)}
                </LinkTableCell>
                <LinkTableCell to={detailLink}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {formatStartTime(call)}
                  </Typography>
                </LinkTableCell>
                <LinkTableCell to={detailLink}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatCallDurationValue(call)}
                  </Typography>
                </LinkTableCell>
                {showConsumptionColumn && (
                  <LinkTableCell to={detailLink}>
                    <CallConsumptionDisplay
                      segments={call.consumptionSegments}
                      activeSegmentId={call.activeSegmentId}
                      activeSegmentType={call.activeSegmentType}
                      callStatus={call.status}
                      fallbackBilledSeconds={getBilledSeconds(call)}
                      fallbackDurationSeconds={getEffectiveSeconds(call)}
                      fallbackBucketType={call.callType === 'ai_agent' ? 'ai' : 'telephony'}
                      compact
                    />
                  </LinkTableCell>
                )}
                {showReverseIvrDigitColumn && (
                  <LinkTableCell to={detailLink}>
                    <Typography variant="body2" color="text.secondary">
                      {renderReverseIvrDigit(call)}
                    </Typography>
                  </LinkTableCell>
                )}
                <LinkTableCell to={detailLink}>
                  {renderStatusChip(call)}
                </LinkTableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
  const pagination = (
    <LinkPagination
      component="div"
      count={totalCalls}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
      rowsPerPageOptions={[10, 25, 50]}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? `${from}-${to} ${t('common:common.of')} ${count}`
          : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
      }
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
    />
  );

  const tableFooter = !loading && calls.length > 0 ? pagination : undefined;

  return (
    <>
      <FilteredListLayout
        headerProps={{
          icon: <PhoneInTalk sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:calls.title'),
          subtitle: t('pages:calls.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={tableContent}
        tableFooter={tableFooter}
      />
      <CreateCallReportDialog
        open={isCreateCallReportOpen}
        onClose={() => setIsCreateCallReportOpen(false)}
        filtersPayload={callReportFiltersPayload}
        filterEntries={callReportSummaryEntries}
        defaultName={defaultCallReportName}
        defaultDescription={defaultCallReportDescription}
        canConfirm={!callReportValidationMessage}
        validationMessage={callReportValidationMessage}
        onCreated={handleCallReportCreated}
      />
    </>
  );
}
